// Notifications — which tracking news go to the recipient by SMS, and the
// text of each. Off by default; one SMS per parcel and notification; texts
// default per language and may use {tracking}, {carrier}, {order}.

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquareText, Pencil, Plus, RotateCcw, TriangleAlert } from 'lucide-react';
import { Card, Meter } from '../../design/ui';
import { EVENTS, renderSms, smsLength } from './api';

const VARIABLE_LABELS = { tracking: 'N° de suivi', carrier: 'Transporteur', order: 'N° de commande' };

const LengthMeter = ({ text, t }) => {
  const { unicode, chars, parts } = smsLength(text);
  const limit = unicode ? (parts > 1 ? 67 * parts : 70) : (parts > 1 ? 153 * parts : 160);
  return (
    <div className={`sms-v2__length${parts > 1 ? ' is-multi' : ''}`}>
      <Meter value={chars} max={limit} tone={parts > 1 ? 'warning' : undefined} label={t('customerSms.events.length', '{{chars}} caractères · {{parts}} SMS', { chars, parts })} />
      <span className="u-num">
        {t('customerSms.events.length', '{{chars}} caractères · {{parts}} SMS', { chars, parts })}
        {unicode && t('customerSms.events.unicode', ' · caractères spéciaux : 70 par SMS')}
      </span>
    </div>
  );
};

// The recipient's SMS switch: a real checkbox exposed as a switch.
const EventSwitch = ({ checked, disabled, onChange, label }) => (
  <label className="u-switch">
    <input type="checkbox" role="switch" aria-checked={checked} checked={checked} disabled={disabled}
      onChange={(e) => onChange(e.target.checked)} aria-label={label} />
    <span className="u-switch__track" aria-hidden="true" />
  </label>
);

const EventRow = ({ event, config, language, samples, canEdit, focused, onFocus, onChange, problemOn, t }) => {
  const [editing, setEditing] = useState(Boolean(config.text));
  const textRef = useRef(null);
  const defaultText = config.default_text?.[language] || '';
  const text = config.text ?? '';
  const effective = text.trim() ? text : defaultText;
  const title = t(`customerSms.events.${event}.title`, event);
  const blocked = config.enabled && !problemOn
    && (event === 'parcel_moving_again' || (event === 'parcel_delivered' && config.only_after_problem !== false));

  const insert = (name) => {
    const token = `{${name}}`;
    const el = textRef.current;
    const base = text || defaultText;
    if (!el) { onChange({ text: `${base} ${token}` }); return; }
    const start = el.selectionStart ?? base.length;
    const end = el.selectionEnd ?? base.length;
    onChange({ text: `${base.slice(0, start)}${token}${base.slice(end)}` });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };

  return (
    <li className={`sms-v2__event${focused ? ' is-focused' : ''}${config.enabled ? ' is-on' : ''}`}
      onFocus={onFocus} onMouseEnter={onFocus}>
      <div className="sms-v2__event-head">
        <EventSwitch checked={config.enabled} disabled={!canEdit} label={title} onChange={(value) => onChange({ enabled: value })} />
        <div className="sms-v2__event-title">
          <strong>{title}</strong>
          <small>{t(`customerSms.events.${event}.when`, '')}</small>
        </div>
        {canEdit && !editing && (
          <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setEditing(true)}>
            <Pencil aria-hidden="true" />
            {t('customerSms.events.edit', 'Personnaliser le texte')}
          </button>
        )}
      </div>

      {blocked && (
        <p className="sms-v2__dependency" role="status">
          <TriangleAlert aria-hidden="true" />
          {t('customerSms.events.needsProblem', 'Ne part qu’après un SMS « Colis en retard », qui est désactivé : activez-le aussi.')}
        </p>
      )}

      {event === 'parcel_delivered' && config.enabled && (
        <div className="sms-v2__scope" role="radiogroup" aria-label={t('customerSms.events.scope', 'Pour quels colis')}>
          {[true, false].map((only) => (
            <label key={String(only)} className="sms-v2__scope-option">
              <input type="radio" name="sms-delivered-scope" checked={config.only_after_problem === only}
                disabled={!canEdit} onChange={() => onChange({ only_after_problem: only })} />
              <span>{only
                ? t('customerSms.events.onlyAfterProblem', 'Seulement si un retard a été annoncé (recommandé)')
                : t('customerSms.events.everyDelivery', 'Pour chaque colis livré')}</span>
            </label>
          ))}
        </div>
      )}

      {editing ? (
        <div className="sms-v2__editor">
          <textarea ref={textRef} className="u-textarea" rows={3} value={text || defaultText} disabled={!canEdit}
            maxLength={300} aria-label={t('channelsUi.sms.textOf', 'Texte du SMS « {{name}} »', { name: title })}
            onChange={(e) => onChange({ text: e.target.value })} />
          <div className="sms-v2__editor-bar">
            <span className="sms-v2__vars">
              {Object.keys(VARIABLE_LABELS).map((name) => (
                <button key={name} type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={!canEdit} onClick={() => insert(name)}
                  title={`{${name}}`}>
                  <Plus aria-hidden="true" />
                  {t(`customerSms.variables.${name}`, VARIABLE_LABELS[name])}
                </button>
              ))}
            </span>
            {canEdit && text && text !== defaultText && (
              <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => onChange({ text: null })}>
                <RotateCcw aria-hidden="true" />
                {t('customerSms.events.reset', 'Revenir au texte par défaut')}
              </button>
            )}
          </div>
          <LengthMeter text={renderSms(effective, samples)} t={t} />
        </div>
      ) : (
        <p className="sms-v2__event-text">{renderSms(effective, samples)}</p>
      )}
    </li>
  );
};

const NotificationsSection = ({ view, draft, onDraft, focusEvent, setFocusEvent }) => {
  const { t } = useTranslation();
  const canEdit = view.can_edit;
  const [from, until] = view.limits?.sending_hours || [8, 20];

  const setEvent = (event, patch) => onDraft({
    events: { ...draft.events, [event]: { ...draft.events[event], ...patch } },
  });

  return (
    <Card
      id="sms-events"
      icon={MessageSquareText}
      title={t('customerSms.events.title', 'Notifications envoyées')}
      description={t('customerSms.events.subtitle', 'Au destinataire du colis, depuis le numéro de mobile de la commande.')}
      actions={(
        <select className="u-select sms-v2__language" value={draft.language} disabled={!canEdit}
          onChange={(e) => onDraft({ language: e.target.value })} aria-label={t('customerSms.language', 'Langue')}>
          <option value="fr">Français</option>
          <option value="en">English</option>
        </select>
      )}
      flush
    >
      <ul className="sms-v2__events">
        {EVENTS.map((event) => (
          <EventRow key={event} event={event} config={{ ...view.events[event], ...draft.events[event] }}
            language={draft.language} samples={view.samples} canEdit={canEdit}
            focused={focusEvent === event} onFocus={() => setFocusEvent(event)}
            onChange={(patch) => setEvent(event, patch)} problemOn={draft.events.parcel_problem.enabled} t={t} />
        ))}
      </ul>
      <ul className="sms-v2__rules">
        <li>{t('customerSms.rules.once', 'Un SMS au plus par colis et par notification.')}</li>
        <li>{t('customerSms.rules.hours', 'Envoyés entre {{from}} h et {{until}} h (heure de Paris) ; la nuit, ils partent à {{from}} h.', { from, until })}</li>
        <li>{t('customerSms.rules.mobile', "Seuls les numéros de mobile valides reçoivent un SMS ; l'indicatif est déduit du pays de livraison.")}</li>
      </ul>
    </Card>
  );
};

export default NotificationsSection;
